(() => {
  if (customElements.get('cordilheira-product-layout')) return;
  const mobileQuery = '(max-width: 999px)';

  class ProductLayout extends HTMLElement {
    connectedCallback() {
      this.events = new AbortController();
      this.mobile = window.matchMedia(mobileQuery);
      this.setupGallerySkeletons();
      this.setupSelectedImageLoading();
      this.setupAddFeedback();
      this.titleBlock = this.querySelector('.product-info [data-block-type="title"]');
      if (this.titleBlock && this.dataset.titleEnabled === 'true') {
        this.placeholder = document.createComment('Native product title position');
        this.titleBlock.before(this.placeholder);
        this.mobile.addEventListener('change', () => this.placeTitle(), { signal: this.events.signal });
        this.placeTitle();
      }
      this.wrapper = this.querySelector('[data-cdlr-thumbnails]');
      this.thumbnails = this.wrapper?.querySelector('.product-gallery__thumbnail-list');
      if (this.thumbnails) {
        const update = () => this.updateOverflow();
        this.thumbnails.addEventListener('scroll', update, { signal: this.events.signal, passive: true });
        this.addEventListener('load', update, { signal: this.events.signal, capture: true });
        this.resizeObserver = new ResizeObserver(update);
        this.resizeObserver.observe(this.thumbnails);
        this.mediaObserver = new MutationObserver(update);
        this.mediaObserver.observe(this.thumbnails, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden'] });
        this.updateOverflow();
      }
    }
    setupGallerySkeletons() {
      const gallery = this.querySelector('[data-cdlr-product-gallery]');
      if (!gallery) return;
      const watched = new WeakSet();
      const selector = '.product-gallery__media[data-media-type="image"] img, .product-gallery__thumbnail > img, .product-info__variant-picker .variant-picker__option-values img';
      const watch = image => {
        if (watched.has(image)) return;
        watched.add(image);
        const galleryImage = gallery.contains(image);
        const finish = success => {
          image.classList.remove('cdlr-image-loading');
          if (!success || !this.mobile.matches || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
          const group = image.closest('.product-gallery__thumbnail-list, .variant-picker__option-values');
          const images = group ? [...group.querySelectorAll('img')] : [];
          const index = group ? Math.max(0, images.indexOf(image)) : 0;
          image.animate([{ opacity: 0 }, { opacity: 1 }], {
            duration: image.closest('.product-gallery__media') ? 250 : 650,
            delay: Math.min(index, 4) * 80, easing: 'ease', fill: 'backwards'
          });
        };
        if (!image.complete) {
          if (galleryImage) image.classList.add('cdlr-image-loading');
          image.addEventListener('load', () => finish(true), { once: true, signal: this.events.signal });
          image.addEventListener('error', () => finish(false), { once: true, signal: this.events.signal });
          if (image.complete) finish(image.naturalWidth > 0);
        } else {
          image.classList.remove('cdlr-image-loading');
        }
      };
      const scan = () => this.querySelectorAll(selector).forEach(watch);
      scan();
      this.skeletonObserver = new MutationObserver(scan);
      this.skeletonObserver.observe(this, { childList: true, subtree: true });
    }
    setupSelectedImageLoading() {
      const gallery = this.querySelector('[data-cdlr-product-gallery]');
      const carousel = gallery?.querySelector('media-carousel');
      const thumbnails = gallery?.querySelector('.product-gallery__thumbnail-list');
      if (!carousel) return;
      const attempts = new WeakMap();
      let timer;
      let activeImage;
      const clear = () => { clearTimeout(timer); timer = null; };
      const retry = image => {
        if (!image.isConnected || image !== activeImage || !navigator.onLine || (image.complete && image.naturalWidth)) return;
        // A slow request is still valid. Retry only an actual failed image.
        if (!image.complete) { schedule(image, 10000); return; }
        const count = attempts.get(image) || 0;
        if (count >= 2) { image.classList.remove('cdlr-image-loading'); return; }
        const source = image.currentSrc || image.src;
        if (!source) return;
        const url = new URL(source, location.href);
        if (url.protocol !== 'https:' && url.protocol !== 'http:') return;
        attempts.set(image, count + 1);
        url.searchParams.set('cdlr_retry', String(count + 1));
        image.classList.add('cdlr-image-loading');
        image.removeAttribute('srcset');
        image.src = url.href;
        schedule(image, 10000);
      };
      const schedule = (image, delay) => {
        clear();
        timer = setTimeout(() => retry(image), delay);
      };
      const activate = slide => {
        const image = slide?.matches('[data-media-type="image"]') ? slide.querySelector('img') : null;
        clear();
        activeImage = image;
        if (!image) return;
        image.loading = 'eager';
        if (image.complete && image.naturalWidth) return;
        if (image.complete) schedule(image, 300);
        else schedule(image, 10000);
      };
      carousel.addEventListener('carousel:select', event => activate(event.detail?.slide), { signal: this.events.signal });
      thumbnails?.addEventListener('click', event => {
        const button = event.target.closest('.product-gallery__thumbnail');
        if (!button || !thumbnails.contains(button)) return;
        const index = [...thumbnails.querySelectorAll('.product-gallery__thumbnail')].indexOf(button);
        activate(carousel.querySelectorAll(':scope > .product-gallery__media')[index]);
      }, { capture: true, signal: this.events.signal });
      gallery.addEventListener('load', event => { if (event.target === activeImage) clear(); }, { capture: true, signal: this.events.signal });
      gallery.addEventListener('error', event => { if (event.target === activeImage) schedule(activeImage, 700); }, { capture: true, signal: this.events.signal });
      window.addEventListener('online', () => { if (activeImage && (!activeImage.complete || !activeImage.naturalWidth)) schedule(activeImage, 300); }, { signal: this.events.signal });
      this.events.signal.addEventListener('abort', clear, { once: true });
    }
    setupAddFeedback() {
      this.addEventListener('variant:add', event => {
        const buttons = event.target.querySelector('buy-buttons');
        const button = buttons?.querySelector('button[type="submit"]');
        const label = button?.querySelector('.cdlr-add-cart-label');
        if (!label) return;
        clearTimeout(this.addFeedbackTimer);
        const original = label.dataset.originalLabel || label.textContent;
        label.dataset.originalLabel = original;
        label.textContent = 'Adicionado ✓';
        button.classList.add('cdlr-added');
        this.addFeedbackTimer = setTimeout(() => {
          if (button.isConnected) { label.textContent = original; button.classList.remove('cdlr-added'); }
          delete label.dataset.originalLabel;
        }, 2200);
      }, { signal: this.events.signal });
    }
    placeTitle() {
      const slot = this.querySelector(`[data-cdlr-title-slot="${this.dataset.titlePosition === 'above' ? 'above' : 'below'}"]`);
      if (this.mobile.matches && slot) slot.append(this.titleBlock);
      else this.placeholder?.after(this.titleBlock);
    }
    updateOverflow() {
      const list = this.thumbnails;
      // Compare physical edges so the white gradients also work for RTL stores.
      const visible = [...list.children].filter(item => !item.hidden);
      const viewport = list.getBoundingClientRect();
      const rects = visible.map(item => item.getBoundingClientRect());
      this.wrapper.dataset.moreLeft = String(rects.some(rect => rect.left < viewport.left - 1));
      this.wrapper.dataset.moreRight = String(rects.some(rect => rect.right > viewport.right + 1));
    }
    disconnectedCallback() {
      this.events?.abort(); clearTimeout(this.addFeedbackTimer); this.resizeObserver?.disconnect(); this.mediaObserver?.disconnect(); this.skeletonObserver?.disconnect();
    }
  }
  customElements.define('cordilheira-product-layout', ProductLayout);

  class ScrollTitle extends HTMLElement {
    connectedCallback() {
      this.events = new AbortController();
      this.mobile = window.matchMedia(mobileQuery);
      this.section = document.getElementById(this.dataset.sectionId);
      if (!this.section) return;
      this.lastY = window.scrollY;
      this.directionUp = false;
      this.suppressed = false;
      this.update = () => {
        const y = Math.max(0, window.scrollY), delta = y - this.lastY;
        if (Math.abs(delta) > 2) { this.directionUp = delta < 0; this.lastY = y; }
        if (y < 100) this.suppressed = false;
        const device = this.dataset.devices;
        const allowed = device === 'all' || (device === 'mobile' ? this.mobile.matches : !this.mobile.matches);
        const visible = allowed && !this.suppressed && y >= Number(this.dataset.threshold || 1000) && (this.dataset.direction === 'both' || this.directionUp);
        const header = document.querySelector('store-header');
        const rect = header?.getBoundingClientRect();
        const headerVisible = header && getComputedStyle(header.closest('.shopify-section')).visibility !== 'hidden';
        const top = headerVisible && rect?.top <= 100 ? Math.max(0, rect.bottom) : 0;
        this.style.setProperty('--cdlr-scroll-top', `${Math.round(top)}px`);
        this.setVisible(visible);
      };
      const schedule = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => { this.frame = 0; this.update(); });
      };
      window.addEventListener('scroll', schedule, { passive: true, signal: this.events.signal });
      window.addEventListener('resize', schedule, { signal: this.events.signal });
      this.mobile.addEventListener('change', schedule, { signal: this.events.signal });
      this.querySelector('button').addEventListener('click', () => {
        const target = this.section.querySelector('.product-info');
        if (!target) return;
        this.suppressed = true;
        this.setVisible(false);
        const header = document.querySelector('store-header');
        const offset = header?.hasAttribute('sticky') ? header.getBoundingClientRect().height : 0;
        target.querySelector('select, input:not([type=hidden]), button')?.focus({ preventScroll: true });
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        window.scrollTo({ top: Math.max(0, target.getBoundingClientRect().top + window.scrollY - offset - 24), behavior: reduced ? 'instant' : 'smooth' });
        // Re-enable after the return animation, and require a fresh scroll direction.
        clearTimeout(this.releaseTimer);
        this.releaseTimer = setTimeout(() => { this.suppressed = false; this.directionUp = false; this.lastY = window.scrollY; }, 1200);
      }, { signal: this.events.signal });
      this.sync = () => {
        const title = this.section.querySelector('[data-cdlr-product-layout] .product-info__title');
        const name = this.querySelector('.cdlr-scroll-title__name');
        if (title && name.textContent !== title.textContent.trim()) name.textContent = title.textContent.trim();
        const source = this.section.querySelector('.product-info sale-price');
        const output = this.querySelector('[data-scroll-price]');
        if (source) {
          const copy = source.cloneNode(true);
          copy.querySelectorAll('.sr-only').forEach(node => node.remove());
          const value = copy.textContent.trim();
          if (output.textContent !== value) output.textContent = value;
        }
      };
      this.observer = new MutationObserver(this.sync);
      this.observer.observe(this.section, { childList: true, subtree: true, characterData: true });
      document.addEventListener('variant:change', event => {
        if (event.target.id !== this.dataset.formId) return;
        const image = this.querySelector('img');
        const media = event.detail?.variant?.featured_media?.preview_image;
        if (image && media?.src) { image.removeAttribute('srcset'); image.src = media.src; }
        this.sync();
      }, { signal: this.events.signal });
      this.sync(); this.update();
    }
    setVisible(visible) {
      if (this.scrollVisible === visible) return;
      this.scrollVisible = visible;
      const wasHidden = this.hidden;
      const currentStyle = wasHidden ? null : getComputedStyle(this);
      const fromOpacity = currentStyle ? Number(currentStyle.opacity) : 0;
      const fromTransform = !currentStyle || currentStyle.transform === 'none' ? 'translateY(0)' : currentStyle.transform;
      this.scrollAnimation?.cancel();
      if (matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.hidden = !visible;
        return;
      }
      if (visible) this.hidden = false;
      if (this.hidden) return;
      this.scrollAnimation = this.animate(
        visible
          ? [{ opacity: wasHidden ? 0 : fromOpacity, transform: wasHidden ? 'translateY(-12px)' : fromTransform }, { opacity: 1, transform: 'translateY(0)' }]
          : [{ opacity: fromOpacity, transform: fromTransform }, { opacity: 0, transform: 'translateY(-12px)' }],
        { duration: 240, easing: 'ease-out', fill: 'both' }
      );
      this.scrollAnimation.finished.then(() => {
        if (!this.scrollVisible) this.hidden = true;
      }).catch(() => {});
    }
    disconnectedCallback() {
      this.events?.abort(); this.observer?.disconnect(); this.scrollAnimation?.cancel(); cancelAnimationFrame(this.frame); clearTimeout(this.releaseTimer);
    }
  }
  customElements.define('cordilheira-product-scroll-title', ScrollTitle);
})();

/* The care/activity app blocks use plain details, so share the layered motion
   without replacing their content or depending on the app's render timing. */
(() => {
  if (window.cdlrAppAccordionMotion) return;
  window.cdlrAppAccordionMotion = true;
  const states = new WeakMap();
  document.addEventListener('click', event => {
    const summary = event.target.closest?.('.product-info .cuidados-produto__cabecalho, .product-info .atividades-produto__cabecalho');
    if (!summary) return;
    const details = summary.parentElement;
    const content = details.querySelector('.cuidados-produto__conteudo, .atividades-produto__conteudo');
    if (!content || details.tagName !== 'DETAILS') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const previous = states.get(details);
    const opening = !(previous ? previous.open : details.open);
    const start = details.getBoundingClientRect().height;
    previous?.animations.forEach(animation => animation.cancel());
    details.dataset.cdlrLayered = 'true';
    details.open = true;
    details.style.height = 'auto';
    const end = opening ? details.getBoundingClientRect().height : summary.getBoundingClientRect().height;
    details.style.height = `${start}px`;
    details.style.overflow = 'hidden';
    details.setAttribute('aria-expanded', String(opening));
    summary.setAttribute('aria-expanded', String(opening));
    content.inert = !opening;
    const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
    const timing = { duration: reduced ? 0 : 460, easing: 'cubic-bezier(.22,.7,.25,1)', fill: 'both' };
    const height = details.animate([{ height: `${start}px` }, { height: `${end}px` }], timing);
    const layers = Array.from(content.children);
    if (!layers.length) layers.push(content);
    const animations = [height, ...layers.map((layer, index) => layer.animate(
      opening
        ? [{ opacity: 0, transform: 'translateY(-8px)' }, { opacity: 1, transform: 'translateY(0)' }]
        : [{ opacity: 1, transform: 'translateY(0)' }, { opacity: 0, transform: 'translateY(-8px)' }],
      { ...timing, duration: reduced ? 0 : 299, delay: reduced ? 0 : (opening ? index : layers.length - 1 - index) / Math.max(1, layers.length - 1) * (opening ? 70 : 40) }
    ))];
    const current = { open: opening, animations };
    states.set(details, current);
    height.finished.then(() => {
      if (states.get(details) !== current) return;
      details.open = opening;
      details.style.height = '';
      details.style.overflow = '';
      animations.forEach(animation => animation.cancel());
      states.delete(details);
    }).catch(() => {});
  }, true);
})();
